import React, { useState } from 'react';
import { forceInfo } from '../data/surveyQuestions';
import ForceModal from './ForceModal';
import WoWLogo from '../icons/WoWLogo.svg';

function WelcomeScreen({ onStart, hasPreviousResults, onBackToResults }) {
  const [selectedForce, setSelectedForce] = useState(null);
  const [isWheelExpanded, setIsWheelExpanded] = useState(false);

  // Force descriptions - you will provide these 6 paragraphs
  const forceDescriptions = {
    s: 'It all starts with a new thought, insight, or creative solution. But an idea alone creates zero outcome. You must move it forward. Creative work becomes powerful when it flows toward Impact',
    a: 'Capture ideas the moment they appear. A napkin sketch. A voice memo. A quick bullet point. If it\'s not noted, it\'s as good as lost. Notes anchor your ideas in memory. Ideas rarely arrive when it\'s convenient. Capture thoughts instantly - without breaking your flow.',
    c: 'Most creative progress happens with others. Share your thoughts - in a meeting, a message, or a brainstorm - to build momentum and refine the idea. Most people just broadcast ideas. High performers share to align. The shift is simple: Don\'t just push information out. Try to adapt, clarify, and connect ideas to the right audience.',
    g: 'Every idea needs direction. Decide what to do next - and in what order. Be the decider, letting only the right actions through. Sometimes the right move is to wait. Timing is also a decision - knowing when to act and when to wait. Most people use Decision process to get recommendations. High performers use it to improve judgment.',
    f: 'This is where impact begins. Nothing matters more than taking action. The smallest step forward beats the most brilliant, unexecuted idea. High performers focus to accelerate execution. The shift is simple: Don\'t stop at ideas. Turn decisions into output - faster, cleaner, and at higher quality.',
    p: 'Creative work doesn\'t follow a perfect schedule. That\'s why reminders matter - to revisit ideas, follow up on decisions, or nudge a task back to life. Keep your hand on the pulse of the flow - the what, when, and who, always at the right time. Most people just set reminders. High performers maintain momentum. The shift is simple: Don\'t just track tasks. Monitor progress, surface drift, and protect focus.',
  };

  const handleForceClick = (forceKey) => {
    setSelectedForce(forceKey);
  };

  const handleCloseModal = () => {
    setSelectedForce(null);
  };

  return (
    <div className="welcome-screen">
      <div className="welcome-container">
        <div className="welcome-header">
          <img src={WoWLogo} alt="Wheel of Work Logo" className="wow-logo-banner" />
          <div>
            <h1>Discover Your Creative Work Footprint</h1>
            <p className="welcome-subtitle">The <strong>Wheel of Work</strong> reveals the six core forces behind all creative work:</p>
          </div>
        </div>

        <div className="welcome-content">
          <div className="welcome-buttons">
            <button className="btn btn-primary btn-large" onClick={onStart}>
              Start Assessment
            </button>
            
            {hasPreviousResults && (
              <button className="btn btn-primary btn-large" onClick={onBackToResults}>
                Back to Results
              </button>
            )}
          </div>

          <div className="wheel-expandable-container">
            <button 
              className="wheel-expandable-header"
              onClick={() => setIsWheelExpanded(!isWheelExpanded)}
              aria-expanded={isWheelExpanded}
            >
              <span className="wheel-toggle-icon">{isWheelExpanded ? '▼' : '▶'}</span>
              <span className="wheel-header-text">Wheel of Work Context</span>
            </button>

            {isWheelExpanded && (
              <div className="wheel-intro">
                <div className="wheel-description">
                  
                  <h4 className="forces-row-heading">Thinking Forces: Idea & Planning Focus</h4>
              
              <div className="forces-grid">
                <button 
                  className="force-item thinking-force force-button"
                  onClick={() => handleForceClick('s')}
                  aria-label="Learn about The Spark"
                >
                  <img src={forceInfo.s.icon} alt="The Spark" className="force-icon-svg" />
                  <h3>The Spark</h3>
                  <p>Think</p>
                </button>
                
                <button 
                  className="force-item thinking-force force-button"
                  onClick={() => handleForceClick('a')}
                  aria-label="Learn about The Anchor"
                >
                  <img src={forceInfo.a.icon} alt="The Anchor" className="force-icon-svg" />
                  <h3>The Anchor</h3>
                  <p>Note</p>
                </button>
                
                <button 
                  className="force-item thinking-force force-button"
                  onClick={() => handleForceClick('c')}
                  aria-label="Learn about The Connector"
                >
                  <img src={forceInfo.c.icon} alt="The Connector" className="force-icon-svg" />
                  <h3>The Connector</h3>
                  <p>Share</p>
                </button>
              </div>

              <h4 className="forces-row-heading">Doing Forces: Action & Delivery Focus</h4>
              
              <div className="forces-grid">
                <button 
                  className="force-item doing-force force-button"
                  onClick={() => handleForceClick('g')}
                  aria-label="Learn about The Decider"
                >
                  <img src={forceInfo.g.icon} alt="The Decider" className="force-icon-svg" />
                  <h3>The Decider</h3>
                  <p>Decide</p>
                </button>
                
                <button 
                  className="force-item doing-force force-button"
                  onClick={() => handleForceClick('f')}
                  aria-label="Learn about The Builder"
                >
                  <img src={forceInfo.f.icon} alt="The Builder" className="force-icon-svg" />
                  <h3>The Builder</h3>
                  <p>Do</p>
                </button>
                
                <button 
                  className="force-item doing-force force-button"
                  onClick={() => handleForceClick('p')}
                  aria-label="Learn about The Pulse"
                >
                  <img src={forceInfo.p.icon} alt="The Pulse" className="force-icon-svg" />
                  <h3>The Pulse</h3>
                  <p>Remind</p>
                </button>
              </div>

              <p className="wheel-insight">
                <strong>Great creative work happens when both parts stay balanced and in motion.</strong>
              </p>
            </div>
          </div>
            )}
          </div>

          {isWheelExpanded && (
            <div className="welcome-info">
              <div className="info-card">
                <h3>What You'll Discover</h3>
                <ul>
                  <li>Your dominant Creative Work profile</li>
                  <li>Your strengths in creative work</li>
                  <li>Potential growth areas</li>
                  <li>How to leverage your profile in teams</li>
                </ul>
              </div>

              <div className="info-card">
                <h3>Time & Content</h3>
                <p><strong>Duration:</strong> 5-10 minutes</p>
                <p><strong>Questions:</strong> 30 quick questions</p>
                <p><strong>Scale:</strong> 5-point response scale</p>
                <p><strong>Result:</strong> Personalized profile + report</p>
              </div>
            </div>
          )}
        </div>

        {/* Force Modal */}
        {selectedForce && (
          <ForceModal 
            force={selectedForce}
            forceInfo={forceInfo}
            content={forceDescriptions[selectedForce]}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </div>
  );
}

export default WelcomeScreen;
